import { useMemo } from "react";
import { seededRng } from "../../utils/symbolGenerator";
import { useSymbolGenerator } from "./SymbolGeneratorContext";

export function SymbolGeneratorViewer() {
    const {
        width,
        height,
        rows,
        cols,
        seed,
        gridDisplacementStrength,
        backgroundColor,
        strokeColor,
        strokeWidth,
        cornerStyle,
        showGridOverlay,
        result,
        handleDownload,
    } = useSymbolGenerator();

    const containerDimensions = useMemo(() => {
        const baseHeight = 520;
        const aspect = width / Math.max(1, height);
        return {
            width: `${Math.min(900, baseHeight * aspect)}px`,
            height: `${baseHeight}px`,
        };
    }, [height, width]);
    const pathDs = result?.pathDs ?? [];
    const dotPoints = result?.dotPoints ?? [];
    const hasRenderableOutput = pathDs.length > 0 || dotPoints.length > 0;
    const colEdges = useMemo(
        () => computeAxisEdges(width, cols, `${seed}|cols`, gridDisplacementStrength),
        [width, cols, seed, gridDisplacementStrength],
    );
    const rowEdges = useMemo(
        () => computeAxisEdges(height, rows, `${seed}|rows`, gridDisplacementStrength),
        [height, rows, seed, gridDisplacementStrength],
    );

    return (
        <div className="panel-viewer blob-field-viewer">
            <div className="path-tool-preview">
                <div className="svg-scroll-wrapper">
                    <div className="svg-container" style={containerDimensions}>
                        <svg
                            viewBox={`0 0 ${width} ${height}`}
                            xmlns="http://www.w3.org/2000/svg"
                            className="path-tool-svg"
                        >
                            <rect width={width} height={height} fill={backgroundColor} />

                            {showGridOverlay && (
                                <g opacity={0.15} stroke="#444" strokeWidth={1}>
                                    {Array.from({ length: cols + 1 }, (_, c) => (
                                        <line
                                            key={`c-${c}`}
                                            x1={colEdges[c] ?? 0}
                                            y1={0}
                                            x2={colEdges[c] ?? 0}
                                            y2={height}
                                        />
                                    ))}
                                    {Array.from({ length: rows + 1 }, (_, r) => (
                                        <line
                                            key={`r-${r}`}
                                            x1={0}
                                            y1={rowEdges[r] ?? 0}
                                            x2={width}
                                            y2={rowEdges[r] ?? 0}
                                        />
                                    ))}
                                </g>
                            )}

                            {hasRenderableOutput ? (
                                <>
                                    {pathDs.map((pathD, idx) => (
                                        <path
                                            key={idx}
                                            d={pathD}
                                            fill="none"
                                            stroke={strokeColor}
                                            strokeWidth={strokeWidth}
                                            strokeLinecap="round"
                                            strokeLinejoin={
                                                cornerStyle === "bevel"
                                                    ? "bevel"
                                                    : cornerStyle === "sharp"
                                                      ? "miter"
                                                      : "round"
                                            }
                                        />
                                    ))}
                                    {dotPoints.map((p, idx) => (
                                        <circle
                                            key={`dot-${idx}`}
                                            cx={p.x}
                                            cy={p.y}
                                            r={Math.max(1, strokeWidth * 0.35)}
                                            fill={strokeColor}
                                        />
                                    ))}
                                </>
                            ) : (
                                <text x={width / 2} y={height / 2} textAnchor="middle" fill="#666">
                                    Click Generate to create a symbol
                                </text>
                            )}
                        </svg>
                    </div>
                </div>
                <div className="path-tool-footer">
                    <p className="path-tool-meta">
                        {result
                            ? `${pathDs.length} paths - ${dotPoints.length} dots - ${result.activeCells} active cells - ${(result.fillRatio * 100).toFixed(1)}% fill`
                            : "No symbol generated"}
                    </p>
                    <button
                        type="button"
                        className="generic-button"
                        onClick={handleDownload}
                        disabled={!hasRenderableOutput}
                    >
                        Download result SVG
                    </button>
                </div>
            </div>
        </div>
    );
}

function computeAxisEdges(
    span: number,
    count: number,
    seed: string,
    displacementStrength: number,
): number[] {
    const n = Math.max(1, Math.floor(count));
    const weights = buildNoisyWeights(n, seed);
    const strength = Math.max(0, Math.min(1, displacementStrength));
    const equal = 1 / n;
    const blended = weights.map((w) => equal + (w - equal) * strength);
    const total = blended.reduce((sum, w) => sum + w, 0) || 1;
    const edges = [0];
    let cursor = 0;
    for (let i = 0; i < n; i += 1) {
        cursor += (blended[i] / total) * span;
        edges.push(cursor);
    }
    return edges;
}

function buildNoisyWeights(count: number, seed: string): number[] {
    const rng = seededRng(seed);
    const base = Array.from({ length: count }, () => 0.15 + rng() * 2.4);
    if (count <= 2) return base;
    const smooth: number[] = [];
    for (let i = 0; i < count; i += 1) {
        const prev = base[(i - 1 + count) % count] ?? base[i]!;
        const curr = base[i]!;
        const next = base[(i + 1) % count] ?? base[i]!;
        smooth.push(prev * 0.1 + curr * 0.8 + next * 0.1);
    }
    return smooth;
}
